/**
 * Sem Contract aqui — SOCKET_EVENTS.TICKET.TRANSFER e SOCKET_EVENTS.TICKET.LIST
 * não têm rota GET associada 1:1 (TRANSFER é progresso de um job; LIST é
 * emitido por ~20 pontos de escrita diferentes, não uma única rota), então
 * não fazem sentido em `defineContract` (que espera route+method). Só o tipo
 * do payload, aplicado no `.emit()` via `typedEvent<T>()`.
 */

/** Payload de SOCKET_EVENTS.TICKET.TRANSFER — TicketController (job de transferência em massa). */
export type TicketTransferEventPayload =
  | { action: "progress"; current: number; total: number; jobId: string }
  | { action: "completed"; transferred: number; jobId: string }
  | { action: "error"; message: string; jobId: string };

/**
 * Shape mínimo que TicketsListCustom, Ticket, NotificationsPopOver, Kanban e
 * MomentsUser usam hoje (id/status/userId/queueId decidem se o card aparece
 * na tela de quem recebeu o evento — é a checagem que faltava no #4421).
 *
 * O model Ticket no backend tem muito mais coluna, e cada um dos ~20 pontos
 * de escrita carrega um conjunto diferente de associações (`ShowTicketService`
 * inclui contact/queue/user/tags/whatsapp/company; outros pontos reemitem a
 * instância como veio do `.update()`, sem recarregar). Por isso as
 * associações abaixo são opcionais e frouxas — não duplicamos o model aqui
 * até um consumidor precisar de um campo novo, para este arquivo não virar
 * espelho do model e apodrecer sozinho (mesma régua do ContactDTO).
 */
export interface TicketDTO {
  id: number;
  uuid?: string;
  status: string;
  channel?: string;
  isGroup?: boolean;
  contactId?: number;
  userId: number | null;
  queueId: number | null;
  whatsappId?: number | null;
  companyId?: number;
  isBot?: boolean;
  lastMessage?: string | null;
  unreadMessages?: number;
  /**
   * `Date` porque a maioria dos ~20 emissores manda a instância Sequelize
   * crua (não `serializeTicket()`, que é do plugin-sdk e vira string ISO) —
   * o socket.io serializa para string na saída, mas o tipo aqui é o que
   * chega no `.emit()`, não o que sai no fio.
   */
  createdAt?: string | Date;
  updatedAt?: string | Date;
  /**
   * Associações — shape varia por emissor (ver acima). `unknown`, não
   * `Record<string, unknown>`: a maioria dos emissores manda a instância
   * Sequelize (classe, sem index signature), que TS não aceita como
   * `Record` mesmo tendo os campos — e não ganhamos type-check nenhum
   * validando a forma de 5 entidades aqui dentro.
   */
  contact?: unknown;
  queue?: unknown;
  user?: unknown;
  tags?: unknown;
  whatsapp?: unknown;
}

/**
 * Payload de SOCKET_EVENTS.TICKET.LIST — ticket criado/mudou de
 * status/dono/fila/não-lidas, emitido pelos ~20 pontos de escrita do backend
 * (UpdateTicketService, wbotMessageListener, CreateTicketService, jobs...).
 *
 * `action: "delete"` tira o card da tela de quem não deve mais ver;
 * `"update"` entrega/atualiza para quem deve; `"updateUnread"` só zera o
 * contador (não reemite o ticket inteiro). `ticket` é OBRIGATÓRIO em
 * "update" com `id` obrigatório — o #4421-bis achado ao tipar isto foi
 * exatamente um emissor mandando `{ ticket: { ticket, userId } }` aninhado,
 * que não tem `id` na raiz e o front nunca conseguia ler.
 */
export type TicketListEventPayload =
  | { action: "update"; ticket: TicketDTO; ticketId?: number }
  | { action: "delete"; ticketId: number; ticket?: TicketDTO }
  | { action: "updateUnread"; ticketId: number }
  /** Fechamento em massa (#4467): um evento só com os ids, em vez de N `update`. */
  | { action: "bulkClose"; ticketIds: number[] };

/**
 * #4271 — salas por CONEXÃO.
 *
 * O namespace isola empresa de empresa; ele não isola conexão de conexão.
 * Quem só pode ver certas conexões não pode receber o ticket das outras —
 * filtrar no front seria decorativo, porque o payload (nome do contato,
 * prévia da mensagem) já teria chegado na máquina de quem não devia vê-lo.
 *
 * Como funciona, sem precisar conhecer a lista inteira de conexões:
 *  - quem vê TODAS entra em `CONNECTION_ROOM_ALL`;
 *  - quem é restrito entra numa sala por conexão permitida;
 *  - o ticket é emitido para as duas: a sala "todas" e a da conexão dele.
 *
 * O backend entra nessas salas no handshake, a partir do token já verificado
 * mais a permissão lida do banco — nunca por evento vindo do cliente.
 */
export const CONNECTION_ROOM_PREFIX = "conn:";

/** Sala de quem vê todas as conexões. */
export const CONNECTION_ROOM_ALL = `${CONNECTION_ROOM_PREFIX}all`;

export const connectionRoom = (whatsappId: number | string): string =>
  `${CONNECTION_ROOM_PREFIX}${whatsappId}`;

/**
 * Salas que devem receber um evento de um ticket.
 *
 * Ticket SEM conexão (importação antiga, canal removido) vai só para a sala
 * "todas": não há como provar que pertence a uma conexão permitida, e a mesma
 * decisão vale na listagem e no histórico (fail-closed).
 */
export const ticketRooms = (whatsappId?: number | null): string[] =>
  whatsappId === null || whatsappId === undefined
    ? [CONNECTION_ROOM_ALL]
    : [CONNECTION_ROOM_ALL, connectionRoom(whatsappId)];
